import { LegalLayout, Section, Bullets } from "../components/LegalLayout";

const PrivacyPolicy = () => {
  return (
    <LegalLayout
      testid="privacy-policy-page"
      title="CPC Pay Privacy Policy"
      subtitle="How Comprehensive Pain Consultants collects, uses, and protects your information when you use CPC Pay to receive and move funds."
      effectiveDate="June 1, 2026"
    >
      <Section heading="1. Introduction">
        <p>
          This Privacy Policy explains how Comprehensive Pain Consultants ("CPC," "we," "us," or "our")
          handles personal information through CPC Pay (the "Platform"), our payment platform that
          lets vendors and employees link a bank account and receive verified payments. By creating an
          account or using the Platform, you agree to the practices described here.
        </p>
        <p>
          CPC Pay uses Plaid Inc. ("Plaid") to connect your bank account. Your use of Plaid is also
          governed by Plaid's End User Privacy Policy — see our{" "}
          <a href="/plaid-privacy" className="text-emerald-700 underline hover:text-emerald-800">
            Plaid Privacy disclosure
          </a>{" "}
          for details.
        </p>
      </Section>

      <Section heading="2. Information We Collect">
        <p>We collect the following categories of information:</p>
        <Bullets
          items={[
            "Account information — name, email address, phone number, role (vendor/employee/account manager), and password (stored only as a salted hash).",
            "Identity & payee information — legal name and any details required to originate compliant ACH/RTP/Wire payments under NACHA rules.",
            "Bank account information — obtained through Plaid when you link your account: account and routing numbers, institution name, account mask, balances, and verification status. We store Plaid access tokens and account identifiers, not your bank login credentials.",
            "Payment & transaction data — payouts, invoices, transfer status, settlement and reversal history, and related metadata.",
            "Security & authentication data — multi-factor authentication (TOTP) configuration, hashed backup codes, login and MFA event logs, and device/IP metadata used for fraud and abuse prevention.",
            "Usage data — pages visited, actions taken, and basic device/browser information.",
          ]}
        />
      </Section>

      <Section heading="3. How We Use Your Information">
        <Bullets
          items={[
            "To create and administer your account and verify your identity.",
            "To originate, authorize, settle, and reconcile payments across ACH, Same-Day ACH, RTP, and Wire rails.",
            "To comply with NACHA operating rules, anti-fraud, anti-money-laundering, and other legal obligations.",
            "To secure your account with mandatory multi-factor authentication and to detect and prevent fraud or unauthorized access.",
            "To provide customer support and send transactional and security communications (e.g., verification codes, payment notifications).",
            "To maintain audit trails required for financial and compliance recordkeeping.",
          ]}
        />
      </Section>

      <Section heading="4. How We Share Information">
        <p>We do not sell your personal information. We share it only as needed to operate the Platform:</p>
        <Bullets
          items={[
            "Plaid — to link and verify your bank account and to initiate transfers. See our Plaid Privacy disclosure.",
            "Financial institutions & payment networks — your bank and the ACH/RTP/Wire networks, to move funds to or from your account.",
            "Service providers — vetted vendors that help us operate the Platform (e.g., cloud hosting, and email delivery for security codes). These providers are bound by confidentiality and data-protection obligations.",
            "Legal & compliance — when required by law, regulation, subpoena, or to protect the rights, safety, and property of CPC, our users, or the public.",
            "Business transfers — in connection with a merger, acquisition, or sale of assets, subject to this Policy.",
          ]}
        />
      </Section>

      <Section heading="5. Data Security">
        <p>
          We apply administrative, technical, and physical safeguards designed to protect your
          information, including encryption in transit, hashed passwords and backup codes, mandatory
          multi-factor authentication, role-based access controls, and audit logging. Bank
          connections are handled through Plaid's secure infrastructure; we never see or store your
          bank login credentials. No method of transmission or storage is 100% secure, and we cannot
          guarantee absolute security.
        </p>
      </Section>

      <Section heading="6. Data Retention">
        <p>
          We retain your information for as long as your account is active and as required to comply
          with our legal, tax, accounting, and NACHA recordkeeping obligations (generally a minimum of
          two years for transfer records, and longer where the law requires). When information is no
          longer needed, we delete or de-identify it.
        </p>
      </Section>

      <Section heading="7. Your Rights & Choices">
        <Bullets
          items={[
            "Access and correction — you may review and update your account information from your profile, or by contacting us.",
            "Bank disconnection — you may unlink your bank account at any time; you can also manage Plaid connections at my.plaid.com.",
            "Deletion — you may request deletion of your account and personal information, subject to records we must retain for legal/compliance reasons.",
            "Communications — transactional and security messages are required for account operation and cannot be opted out of while your account is active.",
          ]}
        />
        <p>To exercise any of these rights, contact us using the details in Section 10.</p>
      </Section>

      <Section heading="8. Children's Privacy">
        <p>
          CPC Pay is not directed to individuals under 18, and we do not knowingly collect personal
          information from children. If you believe a child has provided us information, please contact
          us so we can delete it.
        </p>
      </Section>

      <Section heading="9. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be posted on this
          page with a revised effective date, and where appropriate we will notify you within the
          Platform or by email.
        </p>
      </Section>

      <Section heading="10. Contact Us">
        <p>
          Questions about this Privacy Policy or your data? Contact Comprehensive Pain Consultants at{" "}
          <a href="mailto:privacy@cpcdoctors.com" className="text-emerald-700 underline hover:text-emerald-800" data-testid="privacy-contact-email">
            privacy@cpcdoctors.com
          </a>{" "}
          or visit{" "}
          <a href="https://www.cpcdoctors.com/" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline hover:text-emerald-800">
            cpcdoctors.com
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
